import { Injectable, signal, computed, effect, inject, OnDestroy } from '@angular/core';
import { DOCUMENT } from '@angular/common';

export type ThemeMode = 'light' | 'dark' | 'auto';
export type ResolvedTheme = 'light' | 'dark';

const THEME_KEY = 'ftg-theme';
const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

@Injectable({ providedIn: 'root' })
export class ThemeService implements OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly media = this.document.defaultView?.matchMedia?.(DARK_MEDIA_QUERY);

  /** Preferencia guardada por el usuario: light / dark / auto (sigue prefers-color-scheme). */
  readonly mode = signal<ThemeMode>(this.loadMode());

  /** Sistema operativo, solo relevante cuando mode() === 'auto'. */
  private readonly systemPrefersDark = signal<boolean>(this.media?.matches ?? false);

  /** Tema realmente aplicado (siempre light|dark, nunca 'auto'). */
  readonly resolved = computed<ResolvedTheme>(() => {
    const mode = this.mode();
    return mode === 'auto' ? (this.systemPrefersDark() ? 'dark' : 'light') : mode;
  });

  private readonly mediaListener = (e: MediaQueryListEvent): void => {
    this.systemPrefersDark.set(e.matches);
  };

  constructor() {
    this.media?.addEventListener('change', this.mediaListener);
    effect(() => {
      this.applyTheme(this.resolved());
    });
  }

  ngOnDestroy(): void {
    this.media?.removeEventListener('change', this.mediaListener);
  }

  setMode(mode: ThemeMode): void {
    this.mode.set(mode);
    localStorage.setItem(THEME_KEY, mode);
  }

  /** Ciclo light -> dark -> auto -> light, usado por el toggle rápido de AppHeader. */
  cycle(): void {
    const next: Record<ThemeMode, ThemeMode> = { light: 'dark', dark: 'auto', auto: 'light' };
    this.setMode(next[this.mode()]);
  }

  private loadMode(): ThemeMode {
    const stored = localStorage.getItem(THEME_KEY);
    return stored === 'dark' || stored === 'light' || stored === 'auto' ? stored : 'auto';
  }

  private applyTheme(theme: ResolvedTheme): void {
    const root = this.document.documentElement;
    root.style.colorScheme = theme;
    root.classList.toggle('dark', theme === 'dark');
  }
}
